import { LocalStorageKeys } from '@/constants/LocalStorageKey.constant';

/**
 * REST client for stored AI conversations (browse / resume / delete).
 *
 * Contract verified live against the branch API on 2026-07-27:
 *   GET    /api/ai/conversations?limit=30 -> { data: [{id,title,updated_at}], meta:{count,limit} }
 *   GET    /api/ai/conversations/{id}     -> { data: {id,title,created_at,updated_at,messages:[…]} }
 *   DELETE /api/ai/conversations/{id}     -> 204
 *   404 { "message": "Conversation not found." } for a stale/foreign id.
 * Same auth as POST /api/ai/chat: Sanctum bearer + X-Current-School-ID.
 */

export interface AiConversationSummary {
  id: number;
  title: string;
  updated_at: string;
}

/**
 * A component the MODEL chose to display (phase 2). The model sends only the
 * type and identifying props — never record data — so each card renders an
 * existing app component that fetches its own data under the signed-in user's
 * permissions.
 */
export interface AiComponentPayload {
  id: string;
  type: string;
  props: Record<string, unknown>;
  title?: string | null;
  /**
   * Quick actions the user may perform on this card, ALREADY filtered by the
   * backend to what this user is permitted to do. Always present (possibly []).
   * Declares what the action is, never how — the frontend owns routing.
   */
  actions?: Array<{ key: string; label: string; icon?: string | null }> | null;
}

export interface AiConversationMessage {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  tool_activity: Array<{ name: string; status?: string }> | null;
  /** Components displayed for this answer; replayed when a conversation resumes. */
  components?: AiComponentPayload[] | null;
  /** Attachment metadata for this turn ({name, mime, size}) — never bytes. */
  attachments?: Array<{ name: string; mime: string; size: number }> | null;
  created_at: string;
}

export interface AiConversationDetail {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  messages: AiConversationMessage[];
}

/** Error carrying the HTTP status so callers can special-case 404 (start fresh). */
export class AiConversationError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'AiConversationError';
    this.status = status;
  }
}

/** VITE_BACKEND_BASE_URL minus the /graphql suffix — same derivation as useAiChat. */
export function aiApiBase(): string {
  const base = (import.meta.env.VITE_BACKEND_BASE_URL as string | undefined) ?? '';
  return base.replace(/\/graphql\/?$/, '');
}

export function aiAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem(LocalStorageKeys.token);
  const schoolId = localStorage.getItem(LocalStorageKeys.currentSchoolId);
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(schoolId ? { 'X-Current-School-ID': schoolId } : {}),
  };
}

export interface AiConversationsApiOptions {
  /** Test seam; defaults to global fetch. */
  fetchFn?: typeof fetch;
  /** Test seam; defaults to the derived API base. */
  baseUrl?: string;
}

async function request<T>(
  path: string,
  init: RequestInit,
  options: AiConversationsApiOptions,
): Promise<T | null> {
  const doFetch = options.fetchFn ?? fetch;
  const base = options.baseUrl ?? aiApiBase();
  const res = await doFetch(`${base}${path}`, { ...init, headers: aiAuthHeaders() });

  if (res.status === 204) return null;

  if (!res.ok) {
    let message = `Request failed (HTTP ${res.status}).`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) message = body.message;
    } catch {
      /* non-JSON error body — keep the generic message */
    }
    throw new AiConversationError(message, res.status);
  }

  return (await res.json()) as T;
}

export async function listConversations(
  limit = 30,
  options: AiConversationsApiOptions = {},
): Promise<AiConversationSummary[]> {
  // Server clamps to 1..100; clamp here too so the URL is always sane.
  const safeLimit = Math.min(100, Math.max(1, Math.trunc(limit) || 30));
  const body = await request<{ data: AiConversationSummary[] }>(
    `/api/ai/conversations?limit=${safeLimit}`,
    { method: 'GET' },
    options,
  );
  return body?.data ?? [];
}

export async function getConversation(
  id: number,
  options: AiConversationsApiOptions = {},
): Promise<AiConversationDetail | null> {
  const body = await request<{ data: AiConversationDetail }>(
    `/api/ai/conversations/${id}`,
    { method: 'GET' },
    options,
  );
  return body?.data ?? null;
}

export async function deleteConversation(
  id: number,
  options: AiConversationsApiOptions = {},
): Promise<void> {
  await request<null>(`/api/ai/conversations/${id}`, { method: 'DELETE' }, options);
}

/**
 * localStorage key for the active conversation, scoped to user + school so a
 * different login (or a school switch) never resumes someone else's chat.
 * Logout runs localStorage.clear(), which drops these too.
 */
export function activeConversationKey(userId: string | number | null | undefined): string {
  const schoolId = localStorage.getItem(LocalStorageKeys.currentSchoolId) ?? 'noschool';
  return `ai_active_conversation:${userId ?? 'anon'}:${schoolId}`;
}

/**
 * localStorage key for the user's stack-vs-replace workspace preference,
 * scoped the same way as the active conversation.
 */
export function workspaceModeKey(userId: string | number | null | undefined): string {
  const schoolId = localStorage.getItem(LocalStorageKeys.currentSchoolId) ?? 'noschool';
  return `ai_workspace_mode:${userId ?? 'anon'}:${schoolId}`;
}
